import type { BetBundle, RuleDefinition } from '@/types';

/**
 * Client-side rule preview — evaluate a DRAFT rule against a real, already-loaded
 * bundle, instantly, as the author types. Mirrors the backend RuleEngine exactly
 * (same JSONata + LiquidJS libraries, same truthiness rule) so what you preview
 * is what the server will do.
 *
 * jsonata + liquidjs are LAZY-loaded here: they only enter the bundle as a chunk
 * fetched when the Rule Builder is opened, so the inspection console stays lean.
 */

export interface PreviewResult {
  /** null while nothing has been evaluated yet. */
  fired: boolean | null;
  matched: unknown;
  message: string;
  expressionError?: string;
  templateError?: string;
}

/** Same truthiness as the engine: empty array/object, 0, '' are NOT matches. */
function isTruthy(value: unknown): boolean {
  if (value === undefined || value === null || value === false) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.length > 0;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return Boolean(value);
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object') {
    const e = err as { code?: string; message?: string; position?: number; token?: string };
    return [
      e.message ?? 'error',
      e.code ? `(${e.code})` : '',
      e.position !== undefined ? `at ${e.position}` : '',
      e.token ? `near "${e.token}"` : '',
    ]
      .filter(Boolean)
      .join(' ');
  }
  return String(err);
}

// Minimal structural types — enough to call the libs without pinning their d.ts.
interface JsonataExpr {
  evaluate(data: unknown, bindings?: Record<string, unknown>): Promise<unknown> | unknown;
}
type JsonataFn = (expr: string) => JsonataExpr;
interface LiquidInstance {
  parseAndRender(template: string, ctx: unknown): Promise<string>;
}
type LiquidCtor = new (opts?: unknown) => LiquidInstance;

let libs: Promise<{ jsonata: JsonataFn; Liquid: LiquidCtor }> | null = null;

function loadLibs() {
  if (!libs) {
    libs = Promise.all([import('jsonata'), import('liquidjs')]).then(([j, l]) => ({
      jsonata: ((j as { default?: JsonataFn }).default ?? j) as unknown as JsonataFn,
      Liquid: (l as { Liquid: LiquidCtor }).Liquid,
    }));
  }
  return libs;
}

export async function evaluateRule(
  rule: Pick<RuleDefinition, 'expression' | 'message' | 'lists'>,
  bundle: BetBundle,
): Promise<PreviewResult> {
  const { jsonata, Liquid } = await loadLibs();
  const liquid = new Liquid({ strictFilters: false, strictVariables: false });

  const out: PreviewResult = { fired: null, matched: undefined, message: '' };

  let matched: unknown;
  try {
    matched = await Promise.resolve(
      jsonata(rule.expression).evaluate({ bundle }, { lists: rule.lists ?? {} }),
    );
  } catch (err) {
    out.expressionError = describe(err);
    return out;
  }

  out.fired = isTruthy(matched);
  out.matched = matched;
  if (!out.fired) return out;

  try {
    out.message = await liquid.parseAndRender(rule.message, {
      bundle,
      matched,
      lists: rule.lists ?? {},
    });
  } catch (err) {
    out.templateError = describe(err);
  }
  return out;
}
